import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select"
import { api_fetch } from "@/api/client.tsx"
import { OrgOption } from "@/api/org_options.tsx"
import { SearchableSelect, SearchableSelectOption } from "@/components/searchable_select"
import { useState, useEffect } from "preact/hooks"
import { Button } from "@/components/ui/button.tsx"
import { X } from "lucide-react"

interface OrgDropdownCellProps {
  fieldName: string
  value: string
  orgId: string
  onUpdate: (value: string) => void
  placeholder?: string
  className?: string
  allowClear?: boolean
  /** Pass true when rendered inside a Dialog so nested popovers behave correctly. */
  insideModal?: boolean
}

export function OrgDropdownCell({ 
  fieldName, 
  value, 
  orgId, 
  onUpdate, 
  placeholder,
  className,
  allowClear = false,
  insideModal = false,
}: OrgDropdownCellProps) {
  const [options, setOptions] = useState<OrgOption[]>([])
  const [loading, setLoading] = useState(true)

  useEffect(() => {
    async function loadOptions() {
      try {
        const result = await api_fetch<OrgOption[]>("/org-options", {
          params: { 
            field_name: fieldName 
          },
          use_cache: true
        })
        if (result.ok) {
          setOptions(result.value.data)
        }
      } catch (error) {
        console.error("Failed to load org options:", error)
      } finally {
        setLoading(false)
      }
    }
    loadOptions()
  }, [fieldName, orgId])
  
  if (loading) {
    return <div className={`text-gray-500 ${className || ''}`}>Loading...</div>
  }

  // Filter and sort active options
  const activeOptions = options
    .filter(option => option.is_active)
    .sort((a, b) => a.display_order - b.display_order)

  // Use SearchableSelect if more than 10 options
  const useSearchable = activeOptions.length > 10

  if (useSearchable) {
    const searchableOptions: SearchableSelectOption[] = activeOptions.map(option => ({
      value: option.option_value,
      label: option.option_value,
    }))

    return (
      <div className={`flex items-center gap-1 ${className || ""}`}>
        <SearchableSelect
          options={searchableOptions}
          value={value}
          onValueChange={onUpdate}
          placeholder={placeholder || `Select ${fieldName}...`}
          searchPlaceholder={`Search ${fieldName}...`}
          emptyMessage={`No ${fieldName} found.`}
          className="flex-1"
          insideModal={insideModal}
        />
        {allowClear && value && (
          <Button type="button" variant="ghost" className="h-9 w-9 p-0" onClick={() => onUpdate("")}>
            <X className="h-4 w-4" />
          </Button>
        )}
      </div>
    )
  }

  return (
    <div className={`flex items-center gap-1 ${className || ""}`}>
      <Select value={value} onValueChange={onUpdate}>
        <SelectTrigger className="flex-1">
          <SelectValue placeholder={placeholder || `Select ${fieldName}...`} />
        </SelectTrigger>
        <SelectContent>
          {activeOptions.map(option => (
            <SelectItem key={option.id} value={option.option_value}>
              {option.option_value}
            </SelectItem>
          ))}
        </SelectContent>
      </Select>
      {allowClear && value && (
        <Button type="button" variant="ghost" className="h-9 w-9 p-0" onClick={() => onUpdate("")}>
          <X className="h-4 w-4" />
        </Button>
      )}
    </div>
  )
}

